<template>
  <BCard>
    <div class="flex w-full justify-between items-center">
      <div class="flex items-center gap-[12px]">
        <BButton
          variant="primary"
          size="sm"
          class="aspect-square w-[44px]"
          @click="$router.push('/komads/dashboard')"
        >
          <span class="k-arrow-left-3 text-lg" />
        </BButton>
        <div class="text-black font-semibold text-[24px]">
          Riwayat Top Up{{ accountType === 'BCAD' ? ' - BCA Digital' : '' }}
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="flex justify-end mt-4 items-center gap-[10px]">
      <DateRangePicker
        ref="picker"
        v-model="dateRange"
        :locale-data="locale"
        :ranges="ranges"
        :opens="'left'"
        :max-date="new Date()"
        class="!min-w-[200px]"
        @select="watchUpdate = true"
        @toggle="(open, _) => open ? watchUpdate = false : false"
      >
        <template v-slot:input="picker">
          <div class="d-flex align-items-center w-100">
            <img
              src="https://storage.googleapis.com/komerce/assets/icons/calendar.png"
              alt="Komerce"
              class="opacity-50"
            >
            <div class="ms-[.5rem]">
              <span
                v-if="
                  YEAR_MONTH_DAY(picker.startDate) === YEAR_MONTH_DAY(today)
                "
                style="color: #828282 !important"
              >
                Hari ini
              </span>
              <span
                v-else-if="
                  YEAR_MONTH_DAY(picker.startDate) === YEAR_MONTH_DAY(last7)
                "
                style="color: #828282 !important"
              >
                7 Hari Terakhir
              </span>
              <span
                v-else-if="
                  YEAR_MONTH_DAY(picker.startDate) ===
                    YEAR_MONTH_DAY(firstDateOfMonth) &&
                    YEAR_MONTH_DAY(picker.endDate) === YEAR_MONTH_DAY(today)
                "
                style="color: #828282 !important"
              >
                Bulan ini
              </span>
              <span
                v-else-if="
                  YEAR_MONTH_DAY(picker.startDate) ===
                    YEAR_MONTH_DAY(komshipDate) &&
                    YEAR_MONTH_DAY(picker.endDate) === YEAR_MONTH_DAY(today)
                "
                style="color: #828282 !important"
              >
                Semua
              </span>
              <span
                v-else-if="
                  !picker.startDate && !picker.endDate
                "
                style="color: #828282 !important"
              >
                Pilih Tanggal
              </span>
              <span
                v-else
                style="color: #11263C !important; text-wrap: nowrap;"
              >
                {{ DDMMYYYY(picker.startDate) }} -
                {{ DDMMYYYY(picker.endDate) }}
              </span>
            </div>
          </div>
        </template>
      </DateRangePicker>
    </div>

    <!-- Table Section -->
    <BOverlay
      :show="isLoading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <div
        id="table"
        class="mt-1"
        :class="historyTopup.length > 20 && 'scrolled-table'"
        @scroll="handleScroll"
      >
        <BTable
          :items="historyTopup"
          :fields="fields"
          show-empty
          empty-text="Tidak ada data yang ditampilkan."
          responsive
          class="rounded-lg border border-solid"
          hover
        >
          <template #cell(numb)="data">
            <span>{{ data.index + 1 }}</span>
          </template>
          <template #cell(created_at)="data">
            <div class="min-w-[200px]">
              <span class="text-black mb-[.25rem]">{{
                DAY_MONTH_YEAR(data.item.created_at)
              }}</span>
              <br>
              <span
                style="color: #828282"
                class="text-sm"
              >
                {{ TIME(data.item.created_at) }} WIB
              </span>
            </div>
          </template>

          <template #cell(account_number)="data">
            <div class="flex flex-col gap-y-[.25rem] min-w-[300px]">
              <span class="text-black">{{ data.item.bank_code }}</span>
              <span class="text-[#828282]">{{ data.item.account_number }}</span>
            </div>
          </template>

          <template #cell(transfer_type)="data">
            <span class="text-black">{{ data.item.type.toLowerCase().replace(/^\w/, char => char.toUpperCase()) }}</span>
          </template>

          <template #cell(amount)="data">
            <span class="text-black inline-block min-w-[150px]">{{ IDR(data.item.amount) }}</span>
          </template>

          <template #cell(status)="data">
            <span
              :class="getStatusClass(data.item.status)"
              style="border-radius: 20px; padding: 0 10px; min-width: 100px !important"
            >
              {{ getStatusText(data.item.status) }}
            </span>
          </template>
        </BTable>
      </div>
    </BOverlay>
  </BCard>
</template>

<script>
import {
  BCard,
  BTable,
  BOverlay,
} from 'bootstrap-vue'
import DateRangePicker from 'vue2-daterange-picker'
import {
  firstDateOfMonth,
  komshipDate,
  last7,
  today,
} from '@/store/helpers'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import {
  LABELDAY,
  LABELMONTH,
  DDMMYYYY,
  YEAR_MONTH_DAY,
  TIME,
  DAY_MONTH_YEAR,
} from '@/libs/filterDate'
import { IDR } from '@/libs/currency'
import _ from 'lodash'
import { newAxiosIns } from '@/libs/axios'
import { statusStyles, columnConfig } from './config'

export default {
  name: 'TopupHistory',
  components: {
    BCard,
    BTable,
    BOverlay,
    DateRangePicker,
  },
  data() {
    return {
      isLoading: false,
      search: '',
      // Pagination properties for infinite scroll
      limit: 25,
      page: 1,
      lastData: false,
      historyTopup: [],
      fields: columnConfig,
      dateRange: { startDate: null, endDate: null },
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
      },
      ranges: {
        'Hari ini': [today, today],
        '7 Hari Terakhir': [last7, today],
        'Bulan ini': [firstDateOfMonth, today],
        'Semua ': [komshipDate, today],
      },
      today,
      last7,
      DDMMYYYY,
      komshipDate,
      firstDateOfMonth,
      YEAR_MONTH_DAY,
      IDR,
      TIME,
      DAY_MONTH_YEAR,
    }
  },
  computed: {
    accountType() {
      return this.$route.params['account-type']
    },
  },
  watch: {
    dateRange: {
      handler() {
        this.getTopupHistory()
      },
    },
  },
  mounted() {
    this.getTopupHistory()
  },
  methods: {
    /**
     * Mengambil data riwayat topup
     */
    async getTopupHistory() {
      this.isLoading = true
      this.page = 1
      this.lastData = false

      const params = {
        limit: this.limit,
        page: this.page,
        start_date: this.dateRange.startDate
          ? YEAR_MONTH_DAY(this.dateRange.startDate)
          : null,
        end_date: this.dateRange.endDate
          ? YEAR_MONTH_DAY(this.dateRange.endDate)
          : null,
        account_type: this.accountType,
      }

      await newAxiosIns
        .get('/komads/api/v1/admin/monit/topup-history', { params })
        .then(res => {
          const { data } = res.data
          this.historyTopup = data
          if (data.length < this.limit) {
            this.lastData = true
          }
        }).finally(() => {
          this.isLoading = false
        })
    },

    /**
     * Handle infinite scroll
     */
    handleScroll(event) {
      const table = event.target || document.getElementById('table')
      if (
        table.scrollTop >= table.scrollHeight - table.offsetHeight - 20
        && !this.isLoading
        && !this.lastData
      ) {
        this.getNextData()
      }
      if (table.scrollTop === 0) {
        setTimeout(() => {
          table.scrollTop = 1
          setTimeout(() => {
            table.scrollTop = 0
          }, 10)
        }, 10)
      }
    },

    /**
     * Get next page of data for infinite scroll
     */
    async getNextData() {
      if (!this.lastData) {
        this.isLoading = true
        this.page += 1

        const params = {
          limit: this.limit,
          page: this.page,
          start_date: this.dateRange.startDate
            ? YEAR_MONTH_DAY(this.dateRange.startDate)
            : null,
          end_date: this.dateRange.endDate
            ? YEAR_MONTH_DAY(this.dateRange.endDate)
            : null,
          account_type: this.accountType,
        }

        await newAxiosIns
          .get('/komads/api/v1/admin/monit/topup-history', { params })
          .then(res => {
            const { data } = res.data
            this.historyTopup.push(...data)
            if (data.length < this.limit) {
              this.lastData = true
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    getStatusText(value) {
      return statusStyles[value].text || ''
    },
    getStatusClass(value) {
      return statusStyles[value].class || ''
    },
  },
}
</script>

<style scoped>
::v-deep .btn-primary {
  border-radius: 10px !important;
}

.scrolled-table {
  height: calc(100vh - 250px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.scrolled-table .table {
  position: relative;
  border-collapse: separate;
  width: 100%;
  table-layout: fixed;
  margin-bottom: 0;
}

/* Memastikan header tabel tetap terlihat dengan baik */
.scrolled-table .table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
}

/* Memastikan baris tabel memiliki tinggi yang cukup */
.scrolled-table .table tbody tr {
  min-height: 50px;
}
</style>
